'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { BrowseNavigationItemType, BrowseNavigationItemStyle } from '../../../events/browse-navigation-item-selected';
import { Course, CourseCategory, Topic } from '@udemy-v2/browse-course';
import { BadgeClass } from '@/generated/graphql';
import { useBadgeClassesByCourseIdQuery } from '@/generated/graphql';
import { isObject } from '@/lib/utils';
export type NavigationItem = CourseCategory | Topic | (BadgeClass & {
  class: 'category' | 'course_category' | 'course_label' | 'course_subcategory' | 'subcategory' | 'topic';
});
export interface CLPNavigationItem {
  id: string;
  type: BrowseNavigationItemType;
  title: string;
  url: string;
  uiRegion: string;
  style: BrowseNavigationItemStyle;
}

/* Priority order is: Primary topic, secondary topics, cert name (if distinct from cert topic name), subcategory, category */
export const typePriority: { [key in BrowseNavigationItemType]: number } = {
  [BrowseNavigationItemType.TOPIC]: 1,
  [BrowseNavigationItemType.BADGE]: 2,
  [BrowseNavigationItemType.SUBCATEGORY]: 3,
  [BrowseNavigationItemType.CATEGORY]: 4,
  [BrowseNavigationItemType.RECOMMENDATION_COLLECTION]: 5
};
export const getNavigationItemId = (navigationItem: NavigationItem) => {
  // For BadgeClass, id is badge topic id
  if ('topic' in navigationItem && navigationItem.topic) {
    return String(navigationItem.topic.id);
  }
  return String(navigationItem.id);
};
export const getNavigationItemType = (navigationItem: NavigationItem): BrowseNavigationItemType | null => {
  // Breadcrumbs include _class field for category, subcategory, topic, and course_label (primary topic)
  if ('_class' in navigationItem) {
    switch (navigationItem._class) {
      case 'category':
      case 'course_category':
        return BrowseNavigationItemType.CATEGORY;
      case 'subcategory':
      case 'course_subcategory':
        return BrowseNavigationItemType.SUBCATEGORY;
      case 'topic':
      case 'course_label':
        return BrowseNavigationItemType.TOPIC;
    }
  }
  // For BadgeClass that contains a topic, type is badge
  if ('topic' in navigationItem && navigationItem.topic) {
    return BrowseNavigationItemType.BADGE;
  }

  // For topics not included in breadcrumbs
  if ('topic_channel_url' in navigationItem) {
    return BrowseNavigationItemType.TOPIC;
  }

  // Shouldn't be able to hit this block, needed for TS
  /**
   * Note: Previously, we were hitting RECOMMENDATION_COLLECTION. We should not return valid items when we don't know
   * what to return so I'm just returning empty string here to make downstream failed if needed.
   */
  return null;
};
export const getNavigationItemHref = (navigationItem: NavigationItem) => {
  // For BadgeClass URL is on nested topic
  if ('topic' in navigationItem && navigationItem.topic) {
    return navigationItem.topic.url;
  }
  // Category / Subcategory
  if ('url' in navigationItem) {
    return navigationItem.url;
  }
  // Topic
  if ('topic_channel_url' in navigationItem) {
    return navigationItem.topic_channel_url;
  }
  // Should be unreachable - needed for TS compilation
  return '';
};
export const getNavigationItemTitle = (navigationItem: NavigationItem) => {
  // For Badge, use name of badge
  if ('topic' in navigationItem && navigationItem.name) {
    return navigationItem.name;
  }
  // For Category / Subcategory, use title
  if ('title' in navigationItem) {
    return navigationItem.title;
  }
  // For Topic, use name
  if ('name' in navigationItem) {
    return navigationItem.name;
  }
  // Should be unreachable - needed for TS compilation
  return '';
};
const addClass = <T,>(classType: string, item: T) => {
  if (isObject(item)) {
    return {
      _class: classType,
      ...item
    };
  }
  return item;
};
export const useCLPNavigationContext = (course, t0) => {
  const $ = _c(14);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? [] : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const excludeItems = t1;
  course?.id;
  const courseId = course?.id ? String(course.id) : "";
  let t2;
  if ($[2] !== excludeItems) {
    t2 = excludeItems.includes(BrowseNavigationItemType.BADGE);
    $[2] = excludeItems;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const shouldFetchBadges = !t2;
  let t3;
  if ($[4] !== courseId) {
    t3 = {
      courseId
    };
    $[4] = courseId;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = !!courseId && shouldFetchBadges;
  let t5;
  if ($[6] !== t4) {
    t5 = {
      enabled: t4
    };
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data: badgeData
  } = useBadgeClassesByCourseIdQuery(t3, t5);
  let t6;
  bb0: {
    if (!course) {
      let t7;
      if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = [];
        $[8] = t7;
      } else {
        t7 = $[8];
      }
      t6 = t7;
      break bb0;
    }
    const courseBadge = shouldFetchBadges ? badgeData?.badgeClassesByLearningProducts?.[0] as BadgeClass | undefined : null;
    let t7;
    if ($[9] !== course?.categories || $[10] !== course?.primaryTopic || $[11] !== course?.topics || $[12] !== courseBadge) {
      const topic = addClass("topic", course?.primaryTopic);
      const topics = course?.topics?.map(_temp) ?? [];
      const categoriesAndSubcategories = course?.categories?.flatMap(_temp3) ?? [];
      t7 = [topic, ...topics, ...categoriesAndSubcategories, courseBadge].filter(Boolean).map(_temp4).filter(_temp5).filter(_temp6).sort(_temp7).slice(0, 5);
      $[9] = course?.categories;
      $[10] = course?.primaryTopic;
      $[11] = course?.topics;
      $[12] = courseBadge;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    t6 = t7;
  }
  return t6;
};
function _temp(topic_0) {
  return addClass("topic", topic_0);
}
function _temp2(subcategory) {
  return addClass("subcategory", subcategory);
}
function _temp3(category) {
  return [addClass("category", category), ...(category.subcategories ?? []).map(_temp2)];
}
function _temp4(navigationItem) {
  return {
    id: getNavigationItemId(navigationItem as NavigationItem),
    type: getNavigationItemType(navigationItem as NavigationItem),
    title: getNavigationItemTitle(navigationItem as NavigationItem) as string,
    url: getNavigationItemHref(navigationItem as NavigationItem),
    uiRegion: "browse_navigation",
    style: BrowseNavigationItemStyle.PILL_LIGHT
  };
}
function _temp5(item) {
  return item.type !== null && item.title && item.url;
}
function _temp6(navItem, index, items) {
  return items.findIndex(navItemCompare => navItemCompare?.title === navItem?.title) === index;
}
function _temp7(a, b) {
  return typePriority[a.type] - typePriority[b.type];
}